import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import {
  ArrowLeft, Building2, MapPin, FileText, Calendar, User,
  AlertTriangle, ShieldAlert, Info, CheckCircle, RefreshCw,
  MessageSquare, ChevronRight, Clock, Tag, Camera,
  TrendingUp, Clipboard, BarChart2, Eye
} from "lucide-react";

/* ─── Constants ─────────────────────────────────────── */
const SEVERITY_CONFIG = {
  critical: { label: "Critical",  color: "bg-red-50 text-red-700",    border: "border-red-200",  dot: "bg-red-500",    icon: AlertTriangle },
  high:     { label: "High",      color: "bg-orange-50 text-orange-700", border: "border-orange-200", dot: "bg-orange-500", icon: ShieldAlert },
  medium:   { label: "Medium",    color: "bg-amber-50 text-amber-700", border: "border-amber-200",dot: "bg-amber-500",  icon: Info },
  low:      { label: "Low",       color: "bg-green-50 text-green-700",  border: "border-green-200", dot: "bg-green-500",  icon: CheckCircle },
};

const FINDING_STATUS_CONFIG = {
  pending:   { label: "Pending Review", color: "text-slate-500" },
  approved:  { label: "Approved",       color: "text-green-600" },
  dismissed: { label: "Dismissed",      color: "text-slate-400" },
};

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp:        "Workers' Comp",
  habitational:        "Habitational",
  general_liability:   "General Liability",
  commercial_auto:     "Commercial Auto",
};

const STATUS_CONFIG = {
  pending:     { label: "Pending",      color: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress",  color: "bg-blue-50 text-blue-700" },
  completed:   { label: "Needs Review", color: "bg-amber-50 text-amber-700" },
  reviewed:    { label: "Reviewed",     color: "bg-purple-50 text-purple-700" },
  finalized:   { label: "Finalized",    color: "bg-green-50 text-green-700" },
};

/* ─── Risk Gauge ─────────────────────────────────────── */
function RiskGauge({ score }) {
  if (score === null || score === undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-6">
        <span className="text-slate-400 text-sm italic">Not yet scored</span>
      </div>
    );
  }
  const pct    = Math.min(100, Math.max(0, score));
  const color  = pct <= 40 ? "#16a34a" : pct <= 65 ? "#d97706" : "#dc2626";
  const label  = pct <= 40 ? "Low Risk" : pct <= 65 ? "Moderate Risk" : "High Risk";
  const labelColor = pct <= 40 ? "text-green-600" : pct <= 65 ? "text-amber-600" : "text-red-600";
  const labelBg    = pct <= 40 ? "bg-green-50"    : pct <= 65 ? "bg-amber-50"    : "bg-red-50";

  /* SVG arc gauge */
  const r = 52, cx = 64, cy = 64;
  const startAngle = -Math.PI;
  const endAngle   = 0;
  const totalAngle = endAngle - startAngle;
  const valueAngle = startAngle + (pct / 100) * totalAngle;

  const arcPath = (a1, a2) => {
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const laf = Math.abs(a2 - a1) > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${laf} 1 ${x2} ${y2}`;
  };

  const nx = cx + (r - 14) * Math.cos(valueAngle);
  const ny = cy + (r - 14) * Math.sin(valueAngle);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 128 72" className="w-40 h-20 overflow-visible">
        <path d={arcPath(startAngle, endAngle)} fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
        <path d={arcPath(startAngle, valueAngle)} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
        <circle cx={nx} cy={ny} r="6" fill={color} />
      </svg>
      <div className="text-4xl font-heading font-black -mt-3" style={{ color }}>{score}</div>
      <span className={`mt-1.5 text-xs font-semibold px-3 py-0.5 rounded-full ${labelBg} ${labelColor}`}>{label}</span>
    </div>
  );
}

/* ─── Finding Card ───────────────────────────────────── */
function FindingCard({ finding }) {
  const [expanded, setExpanded] = useState(false);
  // Normalise: DB stores category:"Critical" (capitalised); normalise to lowercase key
  const sevKey = (finding.severity || finding.category || "medium").toLowerCase();
  const sev = SEVERITY_CONFIG[sevKey] || SEVERITY_CONFIG.medium;
  const Icon = sev.icon;
  // Derive status from approved/dismissed booleans (DB schema) or status string
  const rawStatus = finding.status
    || (finding.dismissed ? "dismissed" : finding.approved ? "approved" : "pending");
  const statusCfg = FINDING_STATUS_CONFIG[rawStatus] || FINDING_STATUS_CONFIG.pending;

  return (
    <div
      data-testid={`finding-card-${finding.id}`}
      className={`border rounded-xl overflow-hidden transition-all ${sev.border}`}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{finding.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs ${sev.color.split(" ")[1]} font-medium`}>{sev.label}</span>
            {finding.category && finding.category !== finding.title && (
              <>
                <span className="text-slate-300 text-xs">·</span>
                <span className="text-xs text-slate-400">{finding.category}</span>
              </>
            )}
            <span className="text-slate-300 text-xs">·</span>
            <span className={`text-xs font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {(finding.ai_confidence !== undefined || finding.confidence !== undefined) && (
            <span className="text-xs text-slate-400">
              {Math.round((finding.ai_confidence ?? finding.confidence ?? 0) *
                ((finding.ai_confidence ?? 1) <= 1 ? 100 : 1))}% conf.
            </span>
          )}
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
          <p className="text-sm text-slate-600 leading-relaxed">{finding.description}</p>
          {(finding.corrective_action || finding.recommendation) && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs font-semibold text-blue-700 mb-1">Recommended Action</p>
              <p className="text-xs text-blue-600 leading-relaxed">
                {finding.corrective_action || finding.recommendation}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Chat Bubble ────────────────────────────────────── */
function ChatBubble({ msg }) {
  const isInsured = msg.role === "user";
  const isAI      = msg.role === "assistant";
  const ts        = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  if (!isInsured && !isAI) return null;

  return (
    <div className={`flex gap-2.5 ${isInsured ? "justify-end" : "justify-start"}`}
      data-testid={`chat-bubble-${msg.id}`}>
      {isAI && (
        <div className="w-7 h-7 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-1">
          <BarChart2 className="w-3.5 h-3.5 text-navy" />
        </div>
      )}
      <div className={`max-w-[78%] ${isInsured ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isInsured
            ? "bg-navy text-white rounded-br-sm"
            : "bg-slate-100 text-slate-700 rounded-bl-sm"
        }`}>
          {msg.content}
        </div>
        {msg.image_url && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
            <Camera className="w-3 h-3" />
            <span>Photo attached</span>
          </div>
        )}
        <span className="text-xs text-slate-400 px-1">{ts}</span>
      </div>
      {isInsured && (
        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1">
          <User className="w-3.5 h-3.5 text-slate-500" />
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────── */
export default function PreBriefingView() {
  const { inspectionId } = useParams();
  const navigate         = useNavigate();

  const [inspection, setInspection]   = useState(null);
  const [findings, setFindings]       = useState([]);
  const [messages, setMessages]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [activeTab, setActiveTab]     = useState("findings");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [inspRes, findRes, msgRes] = await Promise.all([
        api.get(`/inspections/${inspectionId}`),
        api.get(`/inspections/${inspectionId}/findings`).catch(() => ({ data: [] })),
        api.get(`/inspections/${inspectionId}/messages`).catch(() => ({ data: [] })),
      ]);
      setInspection(inspRes.data);
      setFindings(findRes.data);
      setMessages(msgRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load inspection.");
    }
    setLoading(false);
  }, [inspectionId]);

  useEffect(() => { load(); }, [load]);

  /* ── Derived data ── */
  const findingsBySeverity = ["critical", "high", "medium", "low"].map(sev => ({
    sev,
    items: findings.filter(f =>
      (f.severity || f.category || "").toLowerCase() === sev
    ),
  })).filter(g => g.items.length > 0);

  const totalFindings = findings.length;
  const criticalCount = findings.filter(f => (f.severity || f.category || "").toLowerCase() === "critical").length;
  const highCount     = findings.filter(f => (f.severity || f.category || "").toLowerCase() === "high").length;
  const insuredMsgs   = messages.filter(m => m.role === "user");

  /* ── Loading / Error ── */
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-navy animate-spin mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Loading pre-briefing...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !inspection) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <AlertTriangle className="w-10 h-10 text-red-400" />
          <p className="text-slate-700 font-medium">{error || "Inspection not found"}</p>
          <button onClick={() => navigate("/lc-dashboard")}
            className="text-navy text-sm underline hover:no-underline">
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  const statusCfg = STATUS_CONFIG[inspection.status] || STATUS_CONFIG.pending;
  const dueLabel  = inspection.due_date
    ? new Date(inspection.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="pre-briefing-view">

        {/* ── Back nav ── */}
        <button
          data-testid="back-to-dashboard-btn"
          onClick={() => navigate("/lc-dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-navy text-sm font-medium transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Work Queue
        </button>

        {/* ── Property Hero ── */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
          data-testid="briefing-property-header">
          <div className="bg-navy px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <p className="text-navy-200 text-xs font-semibold uppercase tracking-widest mb-1 text-white/60">
                  Pre-Visit Briefing
                </p>
                <h1 className="font-heading text-2xl font-bold text-white leading-tight">
                  {inspection.insured_name}
                </h1>
                <p className="text-white/70 text-sm mt-1">
                  {[inspection.property_address, inspection.city, inspection.state].filter(Boolean).join(", ")}
                  {inspection.zip_code ? ` ${inspection.zip_code}` : ""}
                </p>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.color}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                  {statusCfg.label}
                </span>
                {inspection.assigned_to_name && (
                  <span className="inline-flex items-center gap-1.5 text-white/80 text-xs">
                    <User className="w-3 h-3" /> {inspection.assigned_to_name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Meta strip */}
          <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">Type</p>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                {TYPE_LABELS[inspection.inspection_type] || inspection.inspection_type}
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">Policy #</p>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                {inspection.policy_number || <span className="text-slate-400 italic text-xs">Not provided</span>}
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">Ordered</p>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                {inspection.created_at
                  ? new Date(inspection.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                  : "—"}
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">Due Date</p>
              <div className={`flex items-center gap-1.5 text-sm font-medium ${dueLabel ? "text-slate-700" : "text-slate-400"}`}>
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                {dueLabel || <span className="italic text-xs">No due date</span>}
              </div>
            </div>
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: Risk Assessment (1/3) ── */}
          <div className="space-y-5">

            {/* Risk Score Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
              data-testid="risk-score-card">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Risk Assessment</h2>
              </div>
              <RiskGauge score={inspection.risk_score} />
              {inspection.risk_level && (
                <p className="text-center text-xs text-slate-500 mt-3 font-medium">
                  {inspection.risk_level}
                </p>
              )}
            </div>

            {/* Findings Summary Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
              data-testid="findings-summary-card">
              <div className="flex items-center gap-2 mb-4">
                <Clipboard className="w-4 h-4 text-slate-500" />
                <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Findings Summary</h2>
              </div>
              {totalFindings === 0 ? (
                <p className="text-slate-400 text-sm italic text-center py-4">No findings yet</p>
              ) : (
                <div className="space-y-2.5">
                  {["critical", "high", "medium", "low"].map(sev => {
                    const count = findings.filter(f =>
                      (f.severity || f.category || "").toLowerCase() === sev
                    ).length;
                    if (!count) return null;
                    const cfg = SEVERITY_CONFIG[sev];
                    return (
                      <div key={sev} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                          <span className="text-sm text-slate-600">{cfg.label}</span>
                        </div>
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${cfg.color}`}>{count}</span>
                      </div>
                    );
                  })}
                  <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between">
                    <span className="text-sm text-slate-500 font-medium">Total</span>
                    <span className="text-sm font-bold text-slate-700">{totalFindings}</span>
                  </div>
                </div>
              )}
              {(criticalCount > 0 || highCount > 0) && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-xs font-semibold text-red-700">
                    Attention: {criticalCount + highCount} critical/high finding{criticalCount + highCount !== 1 ? "s" : ""} require on-site verification.
                  </p>
                </div>
              )}
            </div>

            {/* Insured Engagement */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
              data-testid="engagement-card">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-4 h-4 text-slate-500" />
                <h2 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Insured Engagement</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="font-heading text-2xl font-bold text-slate-800">{messages.length}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Total messages</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="font-heading text-2xl font-bold text-slate-800">{insuredMsgs.length}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Insured responses</p>
                </div>
              </div>
              {inspection.mode && (
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>Survey mode</span>
                  <span className="font-semibold text-navy">
                    {inspection.mode === "mode1" ? "AI Chat (Mode 1)" : "Survey Form (Mode 2)"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: Findings + Conversation (2/3) ── */}
          <div className="lg:col-span-2 flex flex-col gap-5">

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
              {[
                { key: "findings",      label: "AI Draft Findings",   icon: AlertTriangle, badge: totalFindings },
                { key: "conversation",  label: "Insured Conversation", icon: MessageSquare,  badge: insuredMsgs.length },
              ].map(t => (
                <button
                  key={t.key}
                  data-testid={`briefing-tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                  {t.badge > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      activeTab === t.key ? "bg-navy/10 text-navy" : "bg-slate-200 text-slate-600"
                    }`}>
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Findings Tab ── */}
            {activeTab === "findings" && (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
                data-testid="findings-panel">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-slate-800">AI Draft Findings</h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      AI-generated findings pending field verification
                    </p>
                  </div>
                  <button onClick={load}
                    className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-lg text-slate-400 hover:text-navy hover:border-navy transition-colors"
                    data-testid="refresh-findings-btn">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="p-5">
                  {findingsBySeverity.length === 0 ? (
                    <div className="py-12 text-center">
                      <Clipboard className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-400 font-medium text-sm">No findings generated yet</p>
                      <p className="text-slate-300 text-xs mt-1">
                        Findings appear after the insured completes the AI survey
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {findingsBySeverity.map(({ sev, items }) => {
                        const cfg = SEVERITY_CONFIG[sev];
                        const Icon = cfg.icon;
                        return (
                          <div key={sev}>
                            <div className="flex items-center gap-2 mb-3">
                              <div className={`w-6 h-6 rounded-md flex items-center justify-center ${cfg.color}`}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                {cfg.label} · {items.length}
                              </h3>
                            </div>
                            <div className="space-y-2.5 pl-2">
                              {items.map(f => <FindingCard key={f.id} finding={f} />)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Conversation Tab ── */}
            {activeTab === "conversation" && (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
                data-testid="conversation-panel">
                <div className="px-5 py-4 border-b border-slate-100">
                  <h2 className="font-semibold text-slate-800">Insured Conversation</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Full AI-guided survey transcript — as completed by the insured
                  </p>
                </div>

                <div className="p-5">
                  {messages.length === 0 ? (
                    <div className="py-12 text-center">
                      <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-400 font-medium text-sm">No conversation yet</p>
                      <p className="text-slate-300 text-xs mt-1">
                        The insured hasn't started the AI survey yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1"
                      data-testid="conversation-thread">
                      {messages.map(msg => <ChatBubble key={msg.id} msg={msg} />)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Start On-Site Visit CTA ── */}
            {inspection.dispatch_status !== "visit_completed" && inspection.dispatch_status !== "completed_no_visit" && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between"
                data-testid="start-visit-cta">
                <div>
                  <p className="text-sm font-semibold text-emerald-800">Ready for the site visit?</p>
                  <p className="text-xs text-slate-500 mt-0.5">Verify findings on-site, add field photos, and finalize the report.</p>
                </div>
                <button
                  data-testid="start-field-visit-btn"
                  onClick={() => navigate(`/lc/visit/${inspection.id}`)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm flex-shrink-0"
                >
                  <Clipboard className="w-4 h-4" /> Start On-Site Visit
                </button>
              </div>
            )}

            {/* ── View Full Review CTA (if completed) ── */}
            {["completed", "reviewed", "finalized"].includes(inspection.status) && (
              <div className="bg-navy/5 border border-navy/10 rounded-2xl p-4 flex items-center justify-between"
                data-testid="view-review-cta">
                <div>
                  <p className="text-sm font-semibold text-navy">Survey Complete</p>
                  <p className="text-xs text-slate-500 mt-0.5">Full findings, executive summary and report are ready to view.</p>
                </div>
                <button
                  data-testid="open-full-review-btn"
                  onClick={() => navigate(`/dashboard/review/${inspection.id}`)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-navy text-white rounded-xl text-sm font-medium hover:bg-navy/90 transition-colors shadow-sm flex-shrink-0"
                >
                  <Eye className="w-4 h-4" /> View Full Review
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
